import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import type {
  CatalogOption,
  EditorState,
  QueryFilter,
  QueryHistoryFilter,
  QuerySheetState,
} from '../../types/redux';
import { extractLineAndColumnV2 } from '../../utils';
import type { EditorAction } from './actions';
import {
  QE_CATALOG_ERROR,
  QE_CATALOG_LOADING,
  QE_ERROR,
  QE_LOADING,
  QE_QUERY_FILTER_STATE,
  QE_RESET_QUERY_STATE,
  QE_RESET_REF_ID,
  QE_SET_ACTIVE_INDEX,
  // Catalog types moved from catalog reducer
  QE_SET_CATALOG,
  QE_SET_CONTINUE_ON_ERROR,
  QE_SET_DELTA,
  QE_SET_QHISTORY_DATA,
  QE_SET_QUERY_HISTORY_FILTER,
  QE_SET_QUERY_HISTORY_SEARCH,
  QE_SET_SYSTEM_CATALOG_FLAG,
  QE_SET_WS_DISABLED_MESSAGE,
  QE_SHEET_FETCH_ERROR,
  QE_UPDATE_STATE,
  QUERY_TEXT,
} from './types';

const initialHistory: QueryHistoryFilter = {
  data: {
    end: moment(), //moment().subtract(29, 'days'),
    start: moment().subtract(5, 'minutes'),
    label: 'Last 5 minutes',
  },
  sort: { sortField: 'added_on', sortOrder: -1 },
  selectedRow: 10,
  page: 0,
};

const initialFilter: QueryFilter = {
  filterColumn: '',
  filterText: '',
  isValid: false,
  isInvalid: false,
  feedback: 'Min 3 characters required',
  tags: [],
};

const initialState: EditorState = {
  loading: false,
  data: {},
  qHistory: initialHistory,
  qFilter: initialFilter,
  qData: {},
  error: '',
  sheetFetchError: null,
  activeTab: 0,
  querySheets: {},
  queries: {},
  // Catalog state moved from catalog reducer
  catalog: {
    loading: false,
    data: [],
    error: null,
  },
  // User-level preference, hydrated from IndexedDB at app start. One value for
  // the whole editor so every open sheet stays in step.
  continueOnError: false,
};

interface NewQueryEditorObj {
  requestId: string;
  isLoading: boolean;
  isRunning: boolean;
  showFullEditor: boolean;
  ref_id: string;
  [key: string]: unknown;
}

export const getNewQueryEditorObj = (
  refId: string = uuidv4(),
  resObj?: Record<string, unknown>
): NewQueryEditorObj => {
  const editorData: NewQueryEditorObj = {
    requestId: '',
    isLoading: false,
    isRunning: false,
    showFullEditor: true,
    ref_id: refId,
  };
  return { ...editorData, ...resObj };
};

interface SetDeltaAction {
  tabID: string;
  val: boolean;
}

const setDeltaQE = (state: EditorState, action: SetDeltaAction): EditorState => {
  const { tabID, val } = action;
  const updatedState = cloneDeep(state.querySheets);
  if (!updatedState?.[tabID]) {
    return state;
  }
  updatedState[tabID].isDelta = val;
  return { ...state, querySheets: updatedState };
};

interface ResetRefAction {
  ref_id: string;
  key?: string;
}

const resetRefID = (state: EditorState, action: ResetRefAction): EditorState => {
  const { ref_id, key } = action;
  if (key) {
    const updatedState = cloneDeep(
      (state as unknown as Record<string, unknown>)?.[key] as Record<string, unknown>
    );
    delete updatedState?.[ref_id];
    return { ...state, [key]: updatedState } as EditorState;
  }
  const updatedQueries = cloneDeep(state.queries);
  const updatedQueriesState = cloneDeep(state.queriesState);
  const updatedQueriesStats = cloneDeep(state.queriesStats);
  const updatedQueriesResults = cloneDeep(state.queriesResults);
  delete updatedQueries?.[ref_id];
  delete updatedQueriesStats?.[ref_id];
  delete updatedQueriesState?.[ref_id];
  delete updatedQueriesResults?.[ref_id];
  return {
    ...state,
    queries: updatedQueries,
    queriesStats: updatedQueriesStats,
    queriesResults: updatedQueriesResults,
    queriesState: updatedQueriesState,
  };
};

const setWSDisabledForRunQueries = (state: EditorState): EditorState => {
  const queryResults = cloneDeep(state?.queriesResults) || {};
  const queryState = cloneDeep(state?.queriesState) || {};
  Object.values(state.querySheets || {}).forEach((item) => {
    const stateEntry = queryState?.[item.ref_id];
    const { isRunning, isLoading } = stateEntry || {};
    if (isRunning || isLoading) {
      stateEntry!.processError =
        'The workspace has been disabled. Please enable the workspace and re-run the query.';
      stateEntry!.isLoading = false;
      stateEntry!.isRunning = false;
      stateEntry!.processId = '';
      const resultEntry = queryResults?.[item.ref_id];
      Object.keys(resultEntry || {}).forEach((res) => {
        const isExec = ['cancelled', 'failed', 'success']?.includes(
          ((resultEntry?.[res] as { queryStatus?: string })?.queryStatus || '').toLowerCase()
        );
        if (!isExec) {
          (resultEntry![res] as { queryError?: string }).queryError =
            'The workspace has been disabled. Please enable the workspace and re-run the query.';
          (resultEntry![res] as { queryStatus?: string }).queryStatus = 'Failed';
        }
      });
    }
  });
  return { ...state, queriesResults: queryResults, queriesState: queryState };
};

interface UpdateStateAction {
  key: string;
  id?: string;
  data?: Record<string, unknown>;
  deltaID?: string;
  eventObj?: {
    processId?: string;
    eventCount?: number;
    isEvent?: boolean;
    ref_id?: string;
    isResult?: boolean;
  };
}

const handleQEUpdateState = (state: EditorState, action: UpdateStateAction): EditorState => {
  const { key, id, data = {}, deltaID, eventObj = {} } = action;
  const { processId, eventCount, isEvent, ref_id, isResult } = eventObj;

  const queries = state.queries || {};
  const queriesState = state.queriesState || {};
  const queriesResults = state.queriesResults || {};
  const querySheets = state.querySheets || {};

  // Validate event state
  if (isEvent && ref_id) {
    const { processId: qProcessId, processError } = queriesState[ref_id] || {};

    const isInvalidResult = isResult && qProcessId && qProcessId !== processId;
    const isInvalidEvent =
      !isResult && (!qProcessId || processError === 'Cancelled' || qProcessId !== processId);

    if (isInvalidResult || isInvalidEvent) {
      return cloneDeep(state);
    } // fallback copy for safety
  }

  // Patch isRunning for queryStats
  if (key === 'queriesStats' && 'isRunning' in data && !data.isRunning && !data.processError) {
    const nextResultKey = (queries[id!] as { runningQueries?: Record<number, string> })
      ?.runningQueries?.[(eventCount || 0) + 1];
    const { start_time, end_time } =
      (nextResultKey
        ? (queriesResults[id!]?.[nextResultKey] as { start_time?: string; end_time?: string })
        : undefined) || {};
    if (start_time && !end_time) {
      data.isRunning = true;
    }
  }

  // Handle queriesResults update
  if (key === 'queriesResults') {
    if (!id) {
      return cloneDeep(state);
    }

    const resultKey =
      eventCount !== undefined
        ? (queries[id] as { runningQueries?: Record<number, string> })?.runningQueries?.[eventCount]
        : undefined;

    if (!resultKey) {
      if (Object.keys(data).length === 0) {
        return {
          ...state,
          [key]: {
            ...((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>),
            [id]: {},
          },
        } as EditorState;
      }

      return {
        ...state,
        [key]: {
          ...((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>),
          [id]: {
            ...(((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>)?.[
              id
            ] as Record<string, unknown>),
            ...data,
          },
        },
      } as EditorState;
    }

    const existing =
      (
        (state as unknown as Record<string, unknown>)[key] as Record<
          string,
          Record<string, unknown>
        >
      )?.[id]?.[resultKey] || {};
    const newResults = (data.results as unknown[])?.length
      ? [...((existing as { results?: unknown[] }).results || []), ...(data.results as unknown[])]
      : (existing as { results?: unknown[] }).results || [];

    return {
      ...state,
      [key]: {
        ...((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>),
        [id]: {
          ...(((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>)?.[
            id
          ] as Record<string, unknown>),
          [resultKey]:
            Object.keys(data).length === 0
              ? {}
              : {
                  ...existing,
                  ...data,
                  results: newResults,
                  queryError: data.queryError
                    ? extractLineAndColumnV2(data.queryError as string, existing)
                    : (existing as { queryError?: string }).queryError,
                },
        },
      },
    } as EditorState;
  }

  // Direct replace for qSheetList or undefined id
  if (!id || key === 'qSheetList') {
    return {
      ...state,
      [key]: data,
    } as EditorState;
  }

  // Handle querySheets with deltaID
  if (key === 'querySheets' && deltaID) {
    return {
      ...state,
      [key]: {
        ...((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>),
        [id]: {
          ...(((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>)?.[
            id
          ] as Record<string, unknown>),
          ...data,
          isDelta: true,
        },
      },
    } as EditorState;
  }

  // Handle other keys with deltaID
  if (deltaID && key !== 'querySheets') {
    return {
      ...state,
      [key]: {
        ...((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>),
        [id]:
          Object.keys(data).length === 0
            ? {}
            : {
                ...((
                  (state as unknown as Record<string, unknown>)[key] as Record<string, unknown>
                )?.[id] as Record<string, unknown>),
                ...data,
              },
      },
      querySheets: {
        ...querySheets,
        [deltaID]: { ...querySheets[deltaID], isDelta: true },
      } as Record<string, QuerySheetState>,
    } as EditorState;
  }

  // Default case: merge data into key/id
  return {
    ...state,
    [key]: {
      ...((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>),
      [id]:
        Object.keys(data).length === 0
          ? {}
          : {
              ...(((state as unknown as Record<string, unknown>)[key] as Record<string, unknown>)?.[
                id
              ] as Record<string, unknown>),
              ...data,
            },
    },
  } as EditorState;
};

interface QueriesStateAction {
  ref_id: string;
  value: Record<string, unknown>;
}

const handleQueriesState = (state: EditorState, action: QueriesStateAction): EditorState => {
  const { ref_id } = action;
  return {
    ...state,
    queries: {
      ...state.queries,
      [ref_id]: {
        ...state.queries[ref_id],
        ...action.value,
      },
    },
  };
};

interface QueryFilterStateAction {
  tab_id: string;
  value: {
    catalog?: string;
    database?: string;
    cluster?: string;
  };
}

const handleQueryFilterState = (
  state: EditorState,
  action: QueryFilterStateAction
): EditorState => {
  const { tab_id } = action;
  const newState = { ...state } as EditorState & { isDelta?: boolean };
  newState.isDelta = true;
  if (!newState.querySheets[tab_id]) {
    newState.querySheets[tab_id] = { ref_id: '' };
  }
  if ('catalog' in action.value) {
    newState.querySheets[tab_id].catalog = action.value?.catalog;
  }
  if ('database' in action.value) {
    newState.querySheets[tab_id].database = action.value?.database;
  }
  if ('cluster' in action.value) {
    newState.querySheets[tab_id].cluster = action.value?.cluster;
  }
  return newState;
};

export const editor = (state: EditorState = initialState, action: EditorAction): EditorState => {
  switch (action.type) {
    case QE_LOADING:
      return { ...state, loading: action.loading };
    case QE_ERROR:
      return { ...state, error: action.data as string };
    case QE_SHEET_FETCH_ERROR:
      return { ...state, sheetFetchError: action.error };
    case QE_SET_ACTIVE_INDEX:
      return { ...state, activeTab: action.index };
    case QE_RESET_QUERY_STATE:
      return {
        ...state,
        activeTab: 0,
        qHistory: initialHistory,
        qFilter: initialFilter,
        qData: {},
        qSheetList: [],
        queries: {},
        queriesResults: {},
        querySheets: {},
      };
    case QE_SET_QUERY_HISTORY_FILTER:
      return {
        ...state,
        qHistory: { ...state.qHistory, [action.key]: action.value } as QueryHistoryFilter,
      };
    case QE_SET_QUERY_HISTORY_SEARCH:
      return { ...state, qFilter: { ...state.qFilter, ...action.updatedObj } as QueryFilter };
    case QE_SET_DELTA:
      return setDeltaQE(state, action);
    case QE_SET_QHISTORY_DATA:
      return { ...state, qData: action.data };
    case QE_RESET_REF_ID: {
      return resetRefID(state, action);
    }
    case QE_SET_WS_DISABLED_MESSAGE: {
      return setWSDisabledForRunQueries(state);
    }
    case QE_UPDATE_STATE:
      return handleQEUpdateState(state, action);
    case QUERY_TEXT:
      return handleQueriesState(state, action);
    case QE_QUERY_FILTER_STATE:
      return handleQueryFilterState(state, action);

    // Catalog cases (moved from catalog reducer)
    case QE_SET_CATALOG:
      return {
        ...state,
        catalog: {
          ...state.catalog,
          data: (action.data || []) as CatalogOption[], // New format - data is already the array
          loading: false,
          error: null,
        },
      };
    case QE_CATALOG_LOADING:
      return {
        ...state,
        catalog: {
          ...state.catalog,
          loading: action.loading,
        },
      };
    case QE_CATALOG_ERROR:
      return {
        ...state,
        catalog: {
          ...state.catalog,
          loading: false,
          error: action.data as string,
        },
      };
    case QE_SET_CONTINUE_ON_ERROR: {
      const value = 'value' in action && typeof action.value === 'boolean' ? action.value : false;
      return { ...state, continueOnError: value };
    }
    case QE_SET_SYSTEM_CATALOG_FLAG: {
      const enabled =
        'enabled' in action && typeof action.enabled === 'boolean' ? action.enabled : false;
      const tables =
        'tables' in action && Array.isArray(action.tables)
          ? (action.tables as string[])
          : undefined;
      return {
        ...state,
        catalog: {
          ...state.catalog,
          systemCatalogEnabled: enabled,
          systemCatalogTables: tables,
        },
      };
    }

    default:
      return state;
  }
};
